import useSWR from 'swr';
import { captureException } from '@sentry/nextjs';
import { useCurrentAccountId } from '@zapier/identity';
import { isProduction, zodParse } from '../../../../utils';
import { usePrefetchedDataContext } from '../../../../contexts/PrefetchedDataContext';
import { UserProfile, Z_UserProfileResponse } from './types';

export async function fetchUserProfile(): Promise<UserProfile> {
  const response = await fetch('/api/v3/recommendations/user_profile');

  const data = await response
    .json()
    .catch((error) => !isProduction() && console.error(error));

  if (!response.ok) {
    throw new Error(
      `Failed to fetch user profile: ${
        data?.errors ?? ['unknown error'].join(', ')
      } (${response.status})`,
    );
  }

  zodParse(Z_UserProfileResponse, data, 'UserProfileResponse');

  return data.objects[0];
}

export function useUserProfile(shouldFetch = true) {
  const currentAccountId = useCurrentAccountId();

  const { data, error, isLoading } = useSWR(
    `/api/v3/recommendations/user_profile/${shouldFetch}`,
    () => {
      return shouldFetch && !!currentAccountId ? fetchUserProfile() : null;
    },
  );

  const { roles, companySizes } = usePrefetchedDataContext();

  if (error) {
    !isProduction() && console.error(error);
    captureException(error);
  }

  return {
    data: data
      ? {
          ...data,
          role_name: roles.find(({ id }) => id === data.role_id)?.name ?? null,
          company_size_display_range:
            companySizes.find(({ id }) => id === data.company_size_id)
              ?.display_range ?? null,
        }
      : null,
    error,
    isLoading,
  };
}
